'use client';

import { Shield } from 'lucide-react';
import Navbar from '@/components/help-center/Navbar';
import Footer from '@/components/help-center/Footer';
import {
  HeroBackdropLayers,
  cn,
} from '@/components/help-center/latest-power-shared';
import ConductCodesSection from '@/components/help-center/conduta/ConductCodesSection';
import { useCms } from '@/components/painel/cms/cms-context';
import { useHelpAppearance } from '@/lib/help-appearance';
import { PublicPageSkeleton } from '@/components/help-center/PublicPageSkeleton';

function FilledMoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.75 15.07A9.75 9.75 0 0 1 8.93 2.25a.75.75 0 0 0-.9-.9A11.25 11.25 0 1 0 22.65 15.97a.75.75 0 0 0-.9-.9Z" />
    </svg>
  );
}

function FilledSunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 1.75a.75.75 0 0 1 .75.75v1.75a.75.75 0 0 1-1.5 0V2.5A.75.75 0 0 1 12 1.75Zm0 17.5a.75.75 0 0 1 .75.75V21.75a.75.75 0 0 1-1.5 0v-1.75a.75.75 0 0 1 .75-.75ZM22.25 12a.75.75 0 0 1-.75.75h-1.75a.75.75 0 0 1 0-1.5H21.5a.75.75 0 0 1 .75.75ZM4.25 12a.75.75 0 0 1-.75.75H1.75a.75.75 0 0 1 0-1.5H3.5A.75.75 0 0 1 4.25 12Zm15.01-7.26a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 1 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0ZM7.04 16.96a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 0 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0Zm11.22 1.06a.75.75 0 0 1-1.06 0l-1.24-1.24a.75.75 0 1 1 1.06-1.06l1.24 1.24a.75.75 0 0 1 0 1.06ZM7.04 7.04a.75.75 0 0 1-1.06 0L4.74 5.8A.75.75 0 0 1 5.8 4.74l1.24 1.24a.75.75 0 0 1 0 1.06Z" />
    </svg>
  );
}

export default function CondutaPage() {
  const { site, siteReady } = useCms();
  const { appearance, setMode, hydrated, isLight } = useHelpAppearance();

  if (!siteReady) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Navbar />
        <main className="flex-1 bg-[#030023]">
          <PublicPageSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  const { conduct } = site;

  return (
    <div className={cn('flex min-h-screen flex-col', isLight ? 'bg-slate-50' : 'bg-white')}>
      <Navbar />
      <main
        className={cn(
          'flex-1 transition-colors duration-300',
          isLight ? 'bg-[#F4F7FC]' : 'bg-[#030023]'
        )}
      >
        {/* Hero — sempre no visual escuro do vídeo */}
        <section
          className={cn(
            'relative overflow-hidden bg-[#030023]',
            isLight && 'border-b border-blue-900/50'
          )}
        >
          <HeroBackdropLayers className="absolute inset-0" />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030023]"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden>
            <span className="absolute left-[12%] top-[28%] h-1 w-1 rounded-full bg-white/80 shadow-[0_0_6px_rgba(255,255,255,0.8)]" />
            <span className="absolute left-[78%] top-[34%] h-0.5 w-0.5 rounded-full bg-sky-100/70" />
            <span className="absolute left-[22%] top-[62%] h-0.5 w-0.5 rounded-full bg-white/60" />
            <span className="absolute left-[88%] top-[58%] h-1 w-1 rounded-full bg-white/50 shadow-[0_0_8px_rgba(125,211,252,0.5)]" />
            <span className="absolute left-[55%] top-[42%] h-0.5 w-0.5 rounded-full bg-white/50" />
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 pb-12 pt-28 sm:pb-14 sm:pt-32 md:pb-16 md:pt-36">
            <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border border-sky-400/25',
                  'bg-sky-500/10 px-3 py-1 font-inter text-[10px] font-bold uppercase tracking-[0.16em] text-sky-100/90',
                  'backdrop-blur-sm sm:text-[11px]'
                )}
              >
                <Shield className="h-3 w-3 text-sky-300" aria-hidden />
                {conduct.badgeLabel?.trim() || 'Diretrizes da comunidade'}
              </span>

              <div
                className="inline-flex shrink-0 items-center rounded-full border border-sky-400/25 bg-white/[0.06] p-0.5 backdrop-blur-sm"
                role="group"
                aria-label="Alternar aparência do conteúdo"
              >
                <button
                  type="button"
                  onClick={() => setMode('dark')}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
                    appearance === 'dark'
                      ? 'bg-sky-500/25 text-sky-100'
                      : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
                  )}
                  aria-pressed={appearance === 'dark'}
                  aria-label="Modo escuro"
                  title="Modo escuro"
                >
                  <FilledMoonIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setMode('light')}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
                    appearance === 'light'
                      ? 'bg-sky-500/25 text-sky-100'
                      : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
                  )}
                  aria-pressed={appearance === 'light'}
                  aria-label="Modo claro"
                  title="Modo claro"
                >
                  <FilledSunIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="max-w-2xl">
              <h1 className="font-poppins text-[2rem] font-bold leading-[1.15] tracking-tight text-white sm:text-4xl md:text-5xl">
                {conduct.title || 'Código de Conduta'}
              </h1>
              <p className="mt-4 max-w-xl font-poppins text-lg leading-relaxed text-[#B5BEDA] md:text-xl">
                {conduct.subtitle}
              </p>
            </div>
          </div>
        </section>

        <ConductCodesSection
          conduct={conduct}
          appearance={hydrated ? appearance : 'dark'}
        />
      </main>
      <Footer />
    </div>
  );
}
